import * as React from "react"
import { Box } from "@mui/material"

// Imported by path, not through the @components barrel: the barrel also exports these
// components, and going through it would make the module import itself.
import AnnouncementDialog from "@components/AnnouncementDialog"
import AnnouncementFrame, { ANNOUNCEMENT_CTA } from "@components/AnnouncementFrame"
import { useAnnouncementsFeed } from "@hooks"
import { announcementColors } from "Theme/palette"

/**
 * The news popup. Mounted once in the layout, so it shows on whatever page the user lands on.
 *
 * Several pending announcements share one dialog and are paged through instead of stacking
 * three of them on top of each other.
 *
 * This is the interrupting surface, and the bell is the one you can ignore. They are kept
 * apart on purpose: what earns a popup is not what belongs in a history list.
 */
export default function Announcements() {
    const { current, position, total, hasPending, next, dismissAll } = useAnnouncementsFeed()

    if (!hasPending || !current) return null

    const isLast = position >= total

    return (
        <AnnouncementDialog
            isOpen
            onClose={dismissAll}
            title={current.title}
            date={current.startAt}
            cancelText={isLast ? "Close" : "Skip all"}
            successText={isLast ? "Got it" : "Next"}
            disableCloseOnSuccess
            // With a single announcement "Close" and "Got it" are the same button twice over.
            // "Skip all" only means something while there is something left to skip.
            cancelProps={{ onClick: dismissAll, ...(total === 1 && { sx: { display: 'none' } }) }}
            successProps={{ onClick: isLast ? dismissAll : next }}
        >
            {/* The frame reports its own height, so `height` here is only what it shows for
                the instant before the first measurement arrives. Long announcements make the
                dialog scroll, which is what `scroll="paper"` is already there for. */}
            <AnnouncementFrame html={current.body} title={current.title} height={200} />

            {total > 1 && (
                <Box
                    sx={{
                        display: "flex", justifyContent: "center", alignItems: "center",
                        gap: 0.75, pb: 2, pt: 0.5,
                    }}
                    aria-label={`Announcement ${position} of ${total}`}
                >
                    {Array.from({ length: total }, (_, i) => (
                        <Box
                            key={i}
                            sx={{
                                height: 6,
                                // The current one stretches instead of only changing colour:
                                // position stays readable without reading a "1/3".
                                width: i === position - 1 ? 18 : 6,
                                borderRadius: 3,
                                background: i === position - 1
                                    ? ANNOUNCEMENT_CTA
                                    : announcementColors.border,
                                transition: 'width .2s ease, background .2s ease',
                            }}
                        />
                    ))}
                </Box>
            )}
        </AnnouncementDialog>
    )
}
